import {
  type FixedDepartureBlockoutRange,
  type FixedDepartureDetail,
  type FixedDepartureStatus,
} from '@utazzo/shared';
import {
  UzAlert,
  UzButton,
  UzDatePicker,
  UzInput,
  UzNumberInput,
  UzSelect,
  UzTextarea,
} from '@utazzo/ui';
import { CalendarOff, GripVertical, Plus, Trash2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';

import { useSaveFixedDepartureCities, useUpdateFixedDeparture } from '../use-fixed-departures';

import { CityPicker } from '@/components/pickers';
import { useCurrentUser } from '@/features/auth/use-auth';
import { useCurrenciesList } from '@/features/currencies/use-currencies';
import { useOccupancyTypesList } from '@/features/occupancy-types/use-occupancy-types';
import { useSuppliersList } from '@/features/suppliers/use-suppliers';

interface Leg {
  /** Client-only key; city legs are replaced wholesale on save. */
  key: string;
  cityId: string | null;
  nights: number;
}

const NO_SUPPLIER = '__none__';
const NO_CURRENCY = '__none__';
const NO_SHARING = '__none__';

/**
 * The catalog approval lifecycle. An editor may only pick the first two, and
 * only until the template's first approval - the API enforces it, this list
 * just avoids offering a choice that will be rejected.
 */
const EDITOR_STATUS_OPTIONS = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'PENDING_APPROVAL', label: 'Pending approval' },
] as const;

const ADMIN_STATUS_OPTIONS = [
  ...EDITOR_STATUS_OPTIONS,
  { value: 'ACTIVE', label: 'Active (sellable)' },
  { value: 'INACTIVE', label: 'Inactive' },
  { value: 'ARCHIVED', label: 'Archived' },
] as const;

let legSeq = 0;
const newLeg = (): Leg => ({ key: `leg-${(legSeq += 1)}`, cityId: null, nights: 1 });

const toLegs = (fd: FixedDepartureDetail): Leg[] =>
  fd.cities.length
    ? fd.cities.map((c) => ({ key: c.id, cityId: c.cityId, nights: c.nights }))
    : [newLeg()];

/** A blockout range in the editor - client key + the two ISO day strings. */
interface BlockoutRow {
  key: string;
  from: string;
  to: string;
}

let blockoutSeq = 0;
const newBlockout = (): BlockoutRow => ({ key: `bo-${(blockoutSeq += 1)}`, from: '', to: '' });
const toBlockoutRows = (fd: FixedDepartureDetail): BlockoutRow[] =>
  (fd.blockoutDates ?? []).map((b) => ({
    key: `bo-${(blockoutSeq += 1)}`,
    from: b.from,
    to: b.to,
  }));

const ymdToDate = (v: string): Date | undefined => (v ? new Date(`${v}T00:00:00.000Z`) : undefined);
const dateToYmd = (d: Date | null | undefined): string =>
  d ? new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 10) : '';

/** Complete rows only (both ends set), in the API shape, for save + comparison. */
const blockoutPayload = (rows: BlockoutRow[]): FixedDepartureBlockoutRange[] =>
  rows.filter((r) => r.from && r.to).map((r) => ({ from: r.from, to: r.to }));

/**
 * Configure step: the template's identity plus its city + nights structure.
 *
 * There are deliberately NO dates here - a fixed departure is a template, so it
 * carries nights only. Saving the legs regenerates the day rows (nights + 1);
 * days that survive keep the text already written on them.
 */
export const FdConfigureStep = ({
  fd,
  onSaved,
}: {
  fd: FixedDepartureDetail;
  onSaved: () => void;
}): React.JSX.Element => {
  const me = useCurrentUser();
  // Only an admin may move a template into the sellable zone (ADR-0069 D9).
  const statusOptions = me?.role === 'ADMIN' ? ADMIN_STATUS_OPTIONS : EDITOR_STATUS_OPTIONS;
  const updateFd = useUpdateFixedDeparture();
  const saveCities = useSaveFixedDepartureCities(fd.id);

  const [title, setTitle] = useState(fd.title);
  const [status, setStatus] = useState<FixedDepartureStatus>(fd.status);
  const [supplierId, setSupplierId] = useState(fd.supplierId ?? NO_SUPPLIER);
  const [currencyId, setCurrencyId] = useState(fd.currencyId ?? NO_CURRENCY);
  const [notes, setNotes] = useState(fd.notes ?? '');
  const [blockouts, setBlockouts] = useState<BlockoutRow[]>(() => toBlockoutRows(fd));
  const [sharingOccupancyTypeId, setSharingOccupancyTypeId] = useState(
    fd.sharingOccupancyTypeId ?? NO_SHARING,
  );
  const [legs, setLegs] = useState<Leg[]>(() => toLegs(fd));

  const suppliers = useSuppliersList({ page: 1, pageSize: 200, sortDir: 'asc', isActive: true });
  const currencies = useCurrenciesList({ page: 1, pageSize: 200, sortDir: 'asc', isActive: true });
  const occupancies = useOccupancyTypesList({
    page: 1,
    pageSize: 100,
    sortDir: 'asc',
    isActive: true,
  });
  const sharingOptions = [
    { value: NO_SHARING, label: 'Not set' },
    ...(occupancies.data?.data ?? []).map((o) => ({ value: o.id, label: o.name })),
  ];
  const currencyOptions = [
    { value: NO_CURRENCY, label: 'Default currency' },
    ...(currencies.data?.data ?? []).map((c) => ({ value: c.id, label: c.code })),
  ];
  const supplierOptions = [
    { value: NO_SUPPLIER, label: 'No supplier' },
    ...(suppliers.data?.data ?? []).map((sup) => ({ value: sup.id, label: sup.name })),
  ];

  // Re-seed when the server row changes underneath (e.g. after a save refetch).
  useEffect(() => {
    setTitle(fd.title);
    setStatus(fd.status);
    setSupplierId(fd.supplierId ?? NO_SUPPLIER);
    setCurrencyId(fd.currencyId ?? NO_CURRENCY);
    setNotes(fd.notes ?? '');
    setSharingOccupancyTypeId(fd.sharingOccupancyTypeId ?? NO_SHARING);
    setLegs(toLegs(fd));
    setBlockouts(toBlockoutRows(fd));
  }, [fd]);

  const setBlockout = (key: string, patch: Partial<BlockoutRow>): void =>
    setBlockouts((prev) => prev.map((b) => (b.key === key ? { ...b, ...patch } : b)));
  const removeBlockout = (key: string): void =>
    setBlockouts((prev) => prev.filter((b) => b.key !== key));
  // A row is invalid once it has both ends set the wrong way round; a half-filled
  // row is "in progress", not an error (it just won't be saved).
  const blockoutInverted = blockouts.some((b) => b.from && b.to && b.from > b.to);

  const totalNights = legs.reduce((sum, l) => sum + (l.nights || 0), 0);
  const totalDays = totalNights + 1;
  const complete = legs.length > 0 && legs.every((l) => l.cityId && l.nights > 0);
  const willDropDays = fd.days.length > totalDays;

  const setLeg = (key: string, patch: Partial<Leg>): void =>
    setLegs((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const removeLeg = (key: string): void => setLegs((prev) => prev.filter((l) => l.key !== key));
  const move = (index: number, delta: number): void =>
    setLegs((prev) => {
      const target = index + delta;
      const a = prev[index];
      const b = prev[target];
      if (!a || !b) return prev;
      const next = [...prev];
      next[index] = b;
      next[target] = a;
      return next;
    });

  const save = async (): Promise<void> => {
    const nextSupplierId = supplierId === NO_SUPPLIER ? null : supplierId;
    const nextCurrencyId = currencyId === NO_CURRENCY ? null : currencyId;
    const nextSharingId = sharingOccupancyTypeId === NO_SHARING ? null : sharingOccupancyTypeId;
    const nextBlockouts = blockoutPayload(blockouts);
    const blockoutsChanged =
      JSON.stringify(nextBlockouts) !== JSON.stringify(fd.blockoutDates ?? []);
    const masterChanged =
      title.trim() !== fd.title ||
      status !== fd.status ||
      nextSupplierId !== fd.supplierId ||
      nextCurrencyId !== fd.currencyId ||
      (notes.trim() || null) !== fd.notes ||
      nextSharingId !== fd.sharingOccupancyTypeId ||
      blockoutsChanged;
    if (masterChanged) {
      await updateFd.mutateAsync({
        id: fd.id,
        payload: {
          title: title.trim(),
          status,
          supplierId: nextSupplierId,
          currencyId: nextCurrencyId,
          notes: notes.trim() || null,
          sharingOccupancyTypeId: nextSharingId,
          blockoutDates: nextBlockouts,
        },
      });
    }
    if (complete) {
      await saveCities.mutateAsync({
        cities: legs.map((l) => ({ cityId: l.cityId as string, nights: l.nights })),
      });
    }
    onSaved();
  };

  const saving = updateFd.isPending || saveCities.isPending;

  return (
    <div className="space-y-6">
      <section className="rounded-lg border border-zinc-200 bg-white p-5">
        <h2 className="text-sm font-semibold text-zinc-900">Package details</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-[1fr_200px]">
          <UzInput
            label="Package title"
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <UzSelect<FixedDepartureStatus>
            label="Status"
            value={status}
            onValueChange={setStatus}
            options={statusOptions.map((o) => ({ value: o.value, label: o.label }))}
          />
        </div>
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <UzSelect
            label="Supplier"
            value={supplierId}
            onValueChange={setSupplierId}
            options={supplierOptions}
          />
          <UzSelect
            label="Sharing basis"
            value={sharingOccupancyTypeId}
            onValueChange={setSharingOccupancyTypeId}
            options={sharingOptions}
            hint="Drives rooms = adults / basis, and the extra-bed ceiling."
          />
          <UzSelect
            label="Rate currency"
            value={currencyId}
            onValueChange={setCurrencyId}
            options={currencyOptions}
          />
        </div>

        <div className="mt-4">
          <UzTextarea
            label="Vendor remarks"
            rows={4}
            placeholder="Blackout dates, minimum pax, reconfirmation warnings."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>

        <dl className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-zinc-500">
          <div className="flex items-center gap-1.5">
            <dt>Reference</dt>
            <dd className="font-mono text-zinc-700">{fd.reference}</dd>
          </div>
          <div className="flex items-center gap-1.5">
            <dt>Duration</dt>
            <dd className="font-medium text-zinc-700">
              {totalNights}N / {totalDays}D
            </dd>
          </div>
        </dl>
      </section>

      <section className="rounded-lg border border-zinc-200 bg-white p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-zinc-900">Blockout dates</h2>
            <p className="mt-0.5 text-xs text-zinc-500">
              Date ranges this departure does NOT operate (carved out of the validity window or
              departure dates). A trip whose travel dates overlap any range cannot be created. For a
              single day, set the same date in both fields.
            </p>
          </div>
          <UzButton
            size="sm"
            variant="outline"
            onClick={() => setBlockouts((p) => [...p, newBlockout()])}
          >
            <Plus className="mr-1.5 h-3.5 w-3.5" aria-hidden />
            Add range
          </UzButton>
        </div>

        {blockouts.length === 0 ? (
          <div className="mt-4 flex items-center gap-2 rounded-md border border-dashed border-zinc-200 px-3 py-4 text-xs text-zinc-500">
            <CalendarOff className="h-4 w-4 text-zinc-400" aria-hidden />
            No blockout dates. This departure can be sold on any date in its validity window.
          </div>
        ) : (
          <div className="mt-4 space-y-2">
            {blockouts.map((b) => {
              const inverted = Boolean(b.from && b.to && b.from > b.to);
              return (
                <div key={b.key} className="flex items-end gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="mb-1 text-[10px] text-zinc-400 uppercase">From</p>
                    <UzDatePicker
                      displayFormat="dd-MM-yyyy"
                      placeholder="From"
                      value={ymdToDate(b.from)}
                      onValueChange={(d) => setBlockout(b.key, { from: dateToYmd(d) })}
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="mb-1 text-[10px] text-zinc-400 uppercase">To</p>
                    <UzDatePicker
                      displayFormat="dd-MM-yyyy"
                      placeholder="To"
                      value={ymdToDate(b.to)}
                      onValueChange={(d) => setBlockout(b.key, { to: dateToYmd(d) })}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeBlockout(b.key)}
                    className="pb-2 text-zinc-300 transition-colors hover:text-red-600"
                    aria-label="Remove blockout range"
                  >
                    <Trash2 className="h-4 w-4" aria-hidden />
                  </button>
                  {inverted && (
                    <span className="pb-2 text-[10px] text-red-600">From must be ≤ To</span>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {blockoutInverted && (
          <UzAlert tone="destructive" className="mt-4">
            Every blockout range must have its "From" on or before its "To".
          </UzAlert>
        )}
      </section>

      <section className="rounded-lg border border-zinc-200 bg-white p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-zinc-900">Cities and nights</h2>
            <p className="mt-0.5 text-xs text-zinc-500">In travel order. Nights only.</p>
          </div>
          <UzButton size="sm" variant="outline" onClick={() => setLegs((p) => [...p, newLeg()])}>
            <Plus className="mr-1.5 h-3.5 w-3.5" aria-hidden />
            Add city
          </UzButton>
        </div>

        <div className="mt-4 space-y-2">
          {legs.map((leg, index) => (
            <div key={leg.key} className="flex items-end gap-2">
              <div className="flex flex-col items-center gap-0.5 pb-1.5">
                <button
                  type="button"
                  onClick={() => move(index, -1)}
                  disabled={index === 0}
                  className="text-zinc-300 transition-colors hover:text-zinc-600 disabled:opacity-40"
                  aria-label="Move up"
                >
                  <GripVertical className="h-4 w-4" aria-hidden />
                </button>
              </div>
              <div className="min-w-0 flex-1">
                <p className="mb-1 text-[10px] text-zinc-400 uppercase">City {index + 1}</p>
                <CityPicker
                  label={undefined}
                  value={leg.cityId}
                  onValueChange={(v) => setLeg(leg.key, { cityId: v })}
                />
              </div>
              <div className="w-28 shrink-0">
                <p className="mb-1 text-[10px] text-zinc-400 uppercase">Nights</p>
                <UzNumberInput
                  precision={0}
                  min={1}
                  max={60}
                  value={leg.nights}
                  onValueChange={(v) => setLeg(leg.key, { nights: v ?? 1 })}
                />
              </div>
              <UzButton
                variant="ghost"
                size="icon"
                className="mb-0.5 h-9 w-9 text-zinc-400 hover:text-red-600"
                onClick={() => removeLeg(leg.key)}
                disabled={legs.length === 1}
                aria-label="Remove city"
              >
                <Trash2 className="h-4 w-4" aria-hidden />
              </UzButton>
            </div>
          ))}
        </div>

        {willDropDays && (
          <UzAlert tone="warning" className="mt-4">
            This shortens the trip from {fd.numberOfDays} days to {totalDays}. Days {totalDays + 1}-
            {fd.numberOfDays} will be removed along with anything written on them.
          </UzAlert>
        )}
      </section>

      <div className="flex items-center gap-2">
        <UzButton onClick={save} loading={saving} disabled={!complete || blockoutInverted}>
          Save and generate days
        </UzButton>
        {!complete && (
          <span className="text-xs text-zinc-500">Pick a city for every row to continue.</span>
        )}
      </div>
    </div>
  );
};
